import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Code, ExternalLink, Users, Building2, Shield, FileText, RefreshCw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));

export default function BackofficeApiDocs() {
  const { theme } = useOutletContext();
  const isDark = theme === 'dark';
  const [activeTab, setActiveTab] = useState("backoffice");
  const [loading, setLoading] = useState({ backoffice: false, customer: false });

  const openDocs = (type) => {
    setLoading(prev => ({ ...prev, [type]: true }));
    const url = type === 'backoffice' 
      ? `${BACKEND_URL}/api/backoffice/docs`
      : `${BACKEND_URL}/api/docs`;
    window.open(url, '_blank');
    setTimeout(() => setLoading(prev => ({ ...prev, [type]: false })), 1000);
  };

  const openRedoc = (type) => {
    const url = type === 'backoffice'
      ? `${BACKEND_URL}/api/backoffice/redoc`
      : `${BACKEND_URL}/api/redoc`;
    window.open(url, '_blank');
  };

  return (
    <div className="p-4 md:p-6 space-y-4 md:space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div>
        <h1 className={`text-xl md:text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
          API Documentation
        </h1>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
          Interactive API documentation for testing
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="backoffice" className="flex items-center justify-center gap-1 md:gap-2 text-xs md:text-sm px-2">
            <Shield className="w-3 h-3 md:w-4 md:h-4" />
            <span className="hidden sm:inline">Backoffice</span>
            <span className="sm:hidden">Admin</span>
          </TabsTrigger>
          <TabsTrigger value="customer" className="flex items-center justify-center gap-1 md:gap-2 text-xs md:text-sm px-2">
            <Users className="w-3 h-3 md:w-4 md:h-4" />
            <span>Customer</span>
          </TabsTrigger>
        </TabsList>

        {/* Backoffice APIs Tab */}
        <TabsContent value="backoffice" className="mt-4 md:mt-6 space-y-4 md:space-y-6">
          <Card className={`p-4 md:p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
              <div className={`p-2 md:p-3 rounded-lg shrink-0 ${isDark ? 'bg-violet-900/30' : 'bg-violet-100'}`}>
                <Shield className="w-5 h-5 md:w-6 md:h-6 text-violet-500" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <h2 className={`text-base md:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Backoffice API Documentation
                </h2>
                <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Complete API docs including all admin endpoints.
                </p>
                
                <div className="mt-3 md:mt-4 space-y-2 md:space-y-3">
                  <h3 className={`text-xs md:text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Includes:
                  </h3>
                  <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                    {[
                      { icon: Users, label: "Users" },
                      { icon: Shield, label: "Roles" },
                      { icon: Building2, label: "Customers" },
                      { icon: FileText, label: "Invoices" },
                      { icon: Code, label: "Settings" },
                      { icon: Shield, label: "CMS" },
                    ].map((item, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center gap-1.5 md:gap-2 p-1.5 md:p-2 rounded ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}
                      >
                        <item.icon className={`w-3 h-3 md:w-4 md:h-4 shrink-0 ${isDark ? 'text-violet-400' : 'text-violet-500'}`} />
                        <span className={`text-xs truncate ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-2 md:gap-3">
                  <Button 
                    onClick={() => openDocs('backoffice')} 
                    disabled={loading.backoffice}
                    className="w-full sm:w-auto text-xs md:text-sm"
                    size="sm"
                  >
                    {loading.backoffice ? (
                      <RefreshCw className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                    )}
                    Swagger UI
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => openRedoc('backoffice')}
                    className="w-full sm:w-auto text-xs md:text-sm"
                    size="sm"
                  >
                    <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                    ReDoc
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Backoffice API Quick Reference */}
          <Card className={`p-4 md:p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <h3 className={`text-sm md:text-base font-semibold mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Quick Reference
            </h3>
            <div className="space-y-1.5 md:space-y-2 overflow-x-auto">
              {[
                { method: "POST", path: "/api/backoffice/login", desc: "Login" },
                { method: "GET", path: "/api/backoffice/roles", desc: "Roles" },
                { method: "POST", path: "/api/backoffice/roles", desc: "Create" },
                { method: "DELETE", path: "/api/backoffice/roles/{id}", desc: "Delete" },
                { method: "GET", path: "/api/internal/users", desc: "Users" },
              ].map((api, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 md:gap-3 p-1.5 md:p-2 rounded ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}
                >
                  <span className={`px-1.5 md:px-2 py-0.5 text-[10px] md:text-xs font-mono rounded shrink-0 ${
                    api.method === 'GET' ? 'bg-green-500/20 text-green-500' :
                    api.method === 'POST' ? 'bg-blue-500/20 text-blue-500' :
                    api.method === 'DELETE' ? 'bg-red-500/20 text-red-500' :
                    'bg-yellow-500/20 text-yellow-500'
                  }`}>
                    {api.method}
                  </span>
                  <code className={`flex-1 text-[10px] md:text-xs truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {api.path}
                  </code>
                  <span className={`text-[10px] md:text-xs shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    {api.desc}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Customer APIs Tab */}
        <TabsContent value="customer" className="mt-4 md:mt-6 space-y-4 md:space-y-6">
          <Card className={`p-4 md:p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <div className="flex flex-col sm:flex-row items-start gap-3 md:gap-4">
              <div className={`p-2 md:p-3 rounded-lg shrink-0 ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <Users className="w-5 h-5 md:w-6 md:h-6 text-blue-500" />
              </div>
              <div className="flex-1 min-w-0 w-full">
                <h2 className={`text-base md:text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  Customer API Documentation
                </h2>
                <p className={`text-xs md:text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Public APIs visible at <code className={`px-1 rounded text-[10px] md:text-xs ${isDark ? 'bg-slate-700' : 'bg-gray-100'}`}>/api/docs</code>
                </p>
                
                <div className={`mt-3 md:mt-4 p-2 md:p-3 rounded-lg ${isDark ? 'bg-amber-900/20 border border-amber-700' : 'bg-amber-50 border border-amber-200'}`}>
                  <p className={`text-xs md:text-sm ${isDark ? 'text-amber-400' : 'text-amber-700'}`}>
                    Excludes all backoffice & internal APIs
                  </p>
                </div>

                <div className="mt-3 md:mt-4 space-y-2 md:space-y-3">
                  <h3 className={`text-xs md:text-sm font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    Includes:
                  </h3>
                  <div className="grid grid-cols-2 gap-1.5 md:gap-2">
                    {[
                      { label: "Layouts" },
                      { label: "Bookings" },
                      { label: "Events" },
                      { label: "Hold/Release" },
                      { label: "Webhooks" },
                      { label: "API Keys" },
                    ].map((item, idx) => (
                      <div 
                        key={idx}
                        className={`flex items-center gap-1.5 md:gap-2 p-1.5 md:p-2 rounded ${isDark ? 'bg-slate-700/50' : 'bg-gray-50'}`}
                      >
                        <Code className={`w-3 h-3 md:w-4 md:h-4 shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-500'}`} />
                        <span className={`text-xs truncate ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 md:mt-6 flex flex-col sm:flex-row gap-2 md:gap-3">
                  <Button 
                    onClick={() => openDocs('customer')} 
                    disabled={loading.customer} 
                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-xs md:text-sm"
                    size="sm"
                  >
                    {loading.customer ? (
                      <RefreshCw className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2 animate-spin" />
                    ) : (
                      <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                    )}
                    Swagger UI
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => openRedoc('customer')}
                    className="w-full sm:w-auto text-xs md:text-sm"
                    size="sm"
                  >
                    <ExternalLink className="w-3 h-3 md:w-4 md:h-4 mr-1.5 md:mr-2" />
                    ReDoc
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Customer API Quick Reference */}
          <Card className={`p-4 md:p-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}`}>
            <h3 className={`text-sm md:text-base font-semibold mb-3 md:mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Quick Reference
            </h3>
            <div className="space-y-1.5 md:space-y-2 overflow-x-auto">
              {[
                { method: "GET", path: "/api/layouts", desc: "List" },
                { method: "POST", path: "/api/layouts", desc: "Create" },
                { method: "POST", path: "/api/layouts/{id}/hold", desc: "Hold" },
                { method: "POST", path: "/api/layouts/{id}/book", desc: "Book" },
                { method: "POST", path: "/api/auth/login", desc: "Login" },
              ].map((api, idx) => (
                <div 
                  key={idx}
                  className={`flex items-center gap-2 md:gap-3 p-1.5 md:p-2 rounded ${isDark ? 'bg-slate-700/30' : 'bg-gray-50'}`}
                >
                  <span className={`px-1.5 md:px-2 py-0.5 text-[10px] md:text-xs font-mono rounded shrink-0 ${
                    api.method === 'GET' ? 'bg-green-500/20 text-green-500' :
                    api.method === 'POST' ? 'bg-blue-500/20 text-blue-500' :
                    api.method === 'PUT' ? 'bg-yellow-500/20 text-yellow-500' :
                    'bg-red-500/20 text-red-500'
                  }`}>
                    {api.method}
                  </span>
                  <code className={`flex-1 text-[10px] md:text-xs truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                    {api.path}
                  </code>
                  <span className={`text-[10px] md:text-xs shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                    {api.desc}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
